import { useLocation, useNavigate, useParams } from "react-router-dom";
import styles from "./index.module.css";
import {
  colorSeq,
  dfProductData,
  locationMapping,
} from "../../../utils/constant";
import MultiselectDropdown from "../../elements/MultiselectDropdown";
import { useState, useEffect } from "react";
import DateRangePicker from "../../elements/DateRangePicker";
import LineChart from "../../elements/LineChart";
import ToggleButton from "../../elements/ToggleButton";
import { useDispatch, useSelector } from "react-redux";
import {
  getPriceHistoryAsyncThunk,
  setLocationsEmpty,
} from "../../../store/slices/Products/priceHistory.slice";
import { convert_usd_to_nzd } from "../../../utils/conversion";

const prepareChartData = (
  priceHistoryData,
  selectedLocation = [],
  title,
  source,
  currency
) => {
  let filteredData = [];
  selectedLocation.forEach((location) => {
    filteredData.push(priceHistoryData[location]);
  });

  let lineGraphData = [];
  // Get all unique dates first (now includes extended range to today)
  let allDates = getLabels(priceHistoryData, selectedLocation);

  filteredData.forEach((data, index) => {
    let temp = {
      label:
        locationMapping[data[Object.keys(data)[0]][0].website].brand +
        " " +
        locationMapping[data[Object.keys(data)[0]][0].website].location,
      data: [],
      borderColor: colorSeq[index],
      backgroundColor: colorSeq[index],
      borderWidth: 2,
      tension: 0.4,
      pointBackgroundColor: colorSeq[index],
      pointBorderColor: "#fff",
      pointRadius: 5,
    };

    // Create a map of date to price for this product
    const priceMap = new Map();
    data[Object.keys(data)[0]].forEach((prod) => {
      const date = new Date(prod.price_date).toLocaleDateString("en-GB"); // Changed to DD/MM/YYYY format
      priceMap.set(date, convert_usd_to_nzd(prod.product_price, currency));
    });

    // Fill in prices for all dates (including extended range)
    let lastPrice = null;
    allDates.forEach((date) => {
      if (priceMap.has(date)) {
        lastPrice = priceMap.get(date);
      }
      // If no price for this date, use the last known price (extends the line to today)
      temp.data.push(lastPrice);
    });

    lineGraphData.push(temp);
  });

  return lineGraphData;
};

const getLabels = (priceHistoryData, selectedLocation = []) => {
  let dates = new Set();

  let filteredData = [];
  selectedLocation.forEach((location) => {
    filteredData.push(priceHistoryData[location]);
  });

  // Collect all dates from all products
  filteredData.forEach((data) => {
    data[Object.keys(data)[0]].forEach((prod) => {
      let date = new Date(prod.price_date).toLocaleDateString("en-GB"); // Changed to DD/MM/YYYY format
      dates.add(date);
    });
  });

  // Convert to array and sort
  const sortedDates = Array.from(dates).sort(
    (a, b) => new Date(a.split('/').reverse().join('-')) - new Date(b.split('/').reverse().join('-'))
  );

  // If we have dates, extend the range to today
  if (sortedDates.length > 0) {
    const minDate = new Date(sortedDates[0].split('/').reverse().join('-'));
    const maxDate = new Date(sortedDates[sortedDates.length - 1].split('/').reverse().join('-'));
    const today = new Date();
    
    // Only extend if today is after the max date in our data
    if (today > maxDate) {
      const extendedDates = [...sortedDates];
      let currentDate = new Date(maxDate);
      currentDate.setDate(currentDate.getDate() + 1); // Start from day after max date
      
      while (currentDate <= today) {
        const dateString = currentDate.toLocaleDateString("en-GB"); // DD/MM/YYYY format
        extendedDates.push(dateString);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      return extendedDates;
    }
  }

  return sortedDates;
};

const DfPriceHistory = (props) => {
  const { data, loading, error, locations } = useSelector(
    (store) => store.priceHistory
  );
  const { source, currency } = useSelector((store) => store.constant);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [locationSteps, setLocationSteps] = useState([]);
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const { imgURL, title, brand } = useLocation().state;

  //   useEffect(() => {
  //     let locations = dfProductData[0]?.details?.map(
  //       (detail) =>
  //         `${detail?.location?.toLowerCase()} - ${detail?.tag?.toLowerCase()}`
  //     );

  //     setSelectedLocation(locations);
  //     setLocationSteps(locations);
  //   }, []);

  function getPriceHistory(canprod_id) {
    dispatch(getPriceHistoryAsyncThunk(canprod_id));
  }

  useEffect(() => {
    getPriceHistory(params.id);
  }, []);

  const onBack = () => {
    dispatch(setLocationsEmpty());
    navigate(-1);
  };

  const onSelectedLocationChange = (value) => {
    setSelectedLocation(value);
  };
  return (
    <div className={styles["container"]}>
      <div className={styles["sidebar"]}>
        <div className={styles["logo"]}>
          <img src="/logo_white.png" alt="logo white" />
        </div>
        <div className={styles["sidebar_content"]}>
          <div>
            <div>{brand}</div>
          </div>
          <div>
            <img src={imgURL} alt={title} />
          </div>
          <div>{title}</div>
          {/* <div>
                    {dfProductData[params.id]?.pricerange}
                </div> */}
        </div>
        <div className={styles["back"]} onClick={onBack}>
          <div>
            <i class="bi bi-caret-left-square"></i>
            BACK
          </div>
        </div>
      </div>
      <div className={styles["main"]}>
        <div className={styles["head"]}>
          <div>Price History</div>
          <div className={styles["filters"]}>
            {/* <div>
              <div>Discounts</div>
              <div>
                <ToggleButton />
              </div>
            </div> */}
            <div className={styles["location_filter"]}>
              <div>
                Location
                <i class="bi bi-pin"></i>
              </div>
              <div>
                <MultiselectDropdown
                  steps={locations?.length > 0 ? locations : []}
                  name="locations"
                  onChange={onSelectedLocationChange}
                />
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div className={styles["line-chart"]}>
          {selectedLocation.length > 0 && (
            <LineChart
              data={prepareChartData(
                data,
                selectedLocation,
                title,
                source,
                currency
              )}
              labels={getLabels(data, selectedLocation, title)}
            />
          )}
        </div>
        <hr />
        <div className={styles["identifiers"]}>
          <div className={styles["ihead"]}>Identifiers</div>
          <div className={styles["values"]}>
            {selectedLocation.length > 0 && (
              <div>
                {selectedLocation?.map((location, index) => (
                  <div
                    className={styles["value_box"]}
                    style={{
                      color: colorSeq[index],
                      border: `1px solid ${colorSeq[index]}`,
                    }}
                  >
                    {locationMapping[location].brand +
                      " " +
                      locationMapping[location].location}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DfPriceHistory;
